import { useSelector, useDispatch } from 'react-redux';
import React, { useEffect, useState, useCallback } from 'react';
import { getRequest } from '../../helpers/apiHandler';
import DataTable from 'react-data-table-component';
import { Button, Spinner, Alert } from 'react-bootstrap';
import PhoneNumbersTable from './PhoneNumbersTable';
import { showAlert } from '../../store/actions/alertAction';

const WabaNumberList = () => {
  const { access_token } = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();

  const [data, setData] = useState([]);
  const [selectedWabaId, setSelectedWabaId] = useState(null);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isPhoneNumbersLoading, setIsPhoneNumbersLoading] = useState(false);

  const fetchSharedWabaID = useCallback(() => {
    setIsLoading(true);
    setSelectedWabaId(null); // Reset selection when loading starts
    const payload = {
      headers: { Authorization: access_token.token },
    };

    getRequest('/v2/whatsapp/get-shared-business', payload, (res) => {
      if (res.success) {
        setData(res.data.data);
      } else {
        dispatch(
          showAlert(
            'Something went wrong while fetching business. Please try again later.',
            'danger'
          )
        );
      }
      setIsLoading(false);
    });
  }, [access_token.token, dispatch]);

  const fetchPhoneNumbers = useCallback(
    (wabaId) => {
      setIsPhoneNumbersLoading(true);
      const payload = {
        headers: { Authorization: access_token.token },
      };

      getRequest(`/v2/whatsapp/get-phone-numbers?waba_id=${wabaId}`, payload, (res) => {
        if (res.success) {
          setPhoneNumbers(res.data.data);
        } else {
          setPhoneNumbers([]);
          dispatch(showAlert(res.message || 'Failed to fetch phone numbers', 'warning'));
        }
        setIsPhoneNumbersLoading(false);
      });
    },
    [access_token.token, dispatch]
  );

  useEffect(() => {
    fetchSharedWabaID();
  }, [fetchSharedWabaID]);

  useEffect(() => {
    if (selectedWabaId) {
      fetchPhoneNumbers(selectedWabaId);
    } else {
      setPhoneNumbers([]);
    }
  }, [selectedWabaId, fetchPhoneNumbers]);

  const handleWabaIDClick = useCallback((wabaId) => {
    setSelectedWabaId((prevId) => (prevId === wabaId ? null : wabaId));
  }, []);

  const columns = [
    {
      name: 'Scopes',
      selector: (row) => (
        <div className="py-2">
          {row.scopes.map((scope, idx) => (
            <div key={idx} className="text-nowrap mb-1">
              <span className="badge bg-light text-dark">{scope}</span>
            </div>
          ))}
        </div>
      ),
      sortable: true,
      wrap: true,
    },
    {
      name: 'WhatsApp Business ID',
      selector: (row) => (
        <div className="py-2">
          {row.target_ids.map((targetId, idx) => (
            <Button
              key={idx}
              variant={selectedWabaId === targetId ? 'primary' : 'outline-primary'}
              size="sm"
              className="mb-1 me-1"
              onClick={() => handleWabaIDClick(targetId)}
            >
              {targetId}
            </Button>
          ))}
        </div>
      ),
      sortable: true,
      wrap: true,
    },
  ];

  return (
    <div className="mb-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="mb-0">WhatsApp Business Account Number List</h5>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={fetchSharedWabaID}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Spinner size="sm" className="mr-2" />
              Loading...
            </>
          ) : (
            <>
              <i className="fas fa-sync-alt mr-2"></i>
              Refresh
            </>
          )}
        </Button>
      </div>

      <DataTable
        data={data}
        columns={columns}
        pagination
        paginationPerPage={5}
        paginationRowsPerPageOptions={[5, 10, 20]}
        noDataComponent={
          <div className="p-4 text-center text-muted">
            {isLoading ? (
              <div>
                <Spinner size="sm" className="me-2" />
                Loading business accounts...
              </div>
            ) : (
              'No business accounts found'
            )}
          </div>
        }
        progressPending={isLoading}
        dense
      />

      {selectedWabaId && (
        <div className="mt-4">
          <PhoneNumbersTable
            data={phoneNumbers}
            wabaId={selectedWabaId}
            isLoading={isPhoneNumbersLoading}
          />
        </div>
      )}
    </div>
  );
};

export default WabaNumberList;
